import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, Receipt, Globe, CreditCard, Sparkles, CheckCircle2, ArrowRight, Users, Smartphone } from "lucide-react";
import SocialIcon from "../components/SocialIcon";
import SEO from "../components/SEO";
import AppSlider from "../components/AppSlider";

const features = [
    {
        id: "mobile-billing",
        title: "Mobile Invoicing & Omnichannel",
        subtitle: "Instant Dues & Auto Reminders",
        desc: "Generate professional Sales Tax, VAT & GST bills on mobile. Share 1-click invoices via WhatsApp, Email & SMS with instant checkout links and automated reminders.",
        icon: <Smartphone className="w-6 h-6" />,
        color: "emerald",
        metric: "Dues Collection: 3x Faster",
        colSpan: "md:col-span-1",
        items: ["1-Click WhatsApp & Email Bills", "Automated Dues Reminders", "Apple Pay & Stripe Checkout"],
        path: "/features/mobile-billing"
    },
    {
        id: "accounting",
        title: "Financial Intelligence",
        subtitle: "Beyond Basic Accounting",
        desc: "Automate your entire financial workflow. From bank reconciliation to cash flow forecasting, let AI handle the numbers.",
        icon: <BarChart3 className="w-6 h-6" />,
        color: "blue",
        metric: "Cash Flow: +24%",
        colSpan: "md:col-span-1",
        items: ["One-Click Reconciliation", "Predictive Cash Flow", "Global Tax Compliance"],
        path: "/features/accounting"
    },
    {
        id: "expenses",
        title: "Expense Management",
        subtitle: "Control Every Penny",
        desc: "Turn receipts into data instantly. Set smart budgets and reimburse employees in seconds.",
        icon: <Receipt className="w-6 h-6" />,
        color: "emerald",
        metric: "Save 15+ Hours/mo",
        colSpan: "md:col-span-1",
        items: ["OCR Receipt Scanning", "Smart Auto-Categorization", "Instant Reimbursements"],
        path: "/features/expenses"
    },
    {
        id: "website",
        title: "Digital Presence",
        subtitle: "No-Code Website Builder",
        desc: "Launch a stunning, SEO-optimized website in minutes with zero coding skills.",
        icon: <Globe className="w-6 h-6" />,
        color: "purple",
        metric: "SEO Score: 98/100",
        colSpan: "md:col-span-1",
        items: ["No-Code Visual Editor", "AI-Powered Copywriting", "Built-in SEO Tools"],
        path: "/features/websites"
    },
    {
        id: "social",
        title: "Brand Growth",
        subtitle: "Social Media Autopilot",
        desc: "Schedule posts, track engagement, and generate viral content across all channels.",
        icon: <SocialIcon className="w-8 h-8" />,
        color: "pink",
        metric: "Engagement: +45%",
        colSpan: "md:col-span-1",
        items: ["Multi-Channel Sync", "Viral Content Generation", "Audience Analytics"],
        path: "/features/social"
    },
    {
        id: "pos",
        title: "Unified Retail",
        subtitle: "Next-Gen POS System",
        desc: "Seamlessly sync your physical and digital stores in real-time.",
        icon: <CreditCard className="w-6 h-6" />,
        color: "amber",
        metric: "Sync: < 0.1s",
        colSpan: "md:col-span-1",
        items: ["Omnichannel Inventory", "Staff Performance Tracking", "Card Auto-Reconciliation"],
        path: "/features/pos"
    },
    {
        id: "payroll",
        title: "Automated Payroll",
        subtitle: "Effortless Salary Distribution",
        desc: "Automate salary payments, tax withholdings, and compliance. Keep your employees happy and your records perfect.",
        icon: <Users className="w-6 h-6" />,
        color: "indigo",
        metric: "Accuracy: 100%",
        colSpan: "md:col-span-1",
        items: ["Automatic Tax Calculator", "Direct Bank Deposits", "Compliance Protection"],
        path: "/features/payroll"
    }
];

export default function Features() {
    return (
        <div className="min-h-screen bg-base text-primary selection:bg-blue-500/30 font-sans transition-colors duration-300">
            <SEO
                title="Best Business Management Software Features | AI-Powered ERP Suite"
                description="Explore blinqbooks — the best all-in-one business software in 2026. AI accounting, POS system, website builder, payroll, expense tracker & social media manager. Everything your business needs in one platform."
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-24">

                {/* Header Section */}
                <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20 space-y-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-600 dark:text-blue-400 text-sm font-medium"
                    >
                        <Sparkles className="w-4 h-4" />
                        <span>The All-In-One Platform</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold tracking-tight text-primary"
                    >
                        Run your entire business <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 via-blue-500 to-emerald-500">
                            on Autopilot
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-secondary leading-relaxed"
                    >
                        Replace your fragmented toolset with one cohesive operating system.
                        Designed for clarity, built for speed, and powered by AI.
                    </motion.p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, index) => (
                        <FeatureCard key={feature.id} feature={feature} index={index} />
                    ))}
                </div>

                {/* Interactive All Apps Slider */}
                <div className="mt-20 md:mt-28">
                    <AppSlider
                        title="Everything Your Business Needs in One Place"
                        subtitle="Swipe through all blinqbooks apps to see live highlights, metrics, and workflows."
                    />
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-16 md:mt-24 max-w-5xl mx-auto bg-gradient-to-tr from-blue-500/5 via-transparent to-purple-500/5 rounded-2xl sm:rounded-3xl p-8 md:p-16 text-center relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-4 text-primary relative z-10">
                        Ready to streamline your operations?
                    </h2>
                    <p className="text-secondary mb-10 text-lg md:text-xl max-w-2xl mx-auto relative z-10">
                        Join thousands of modern businesses scaling effortlessly on our unified AI platform.
                    </p>
                    <Link to="/signup" className="mx-auto flex items-center justify-center gap-3 px-10 py-4 text-lg w-full sm:w-auto relative z-10 group text-center block rounded-full font-extrabold text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 hover:from-blue-500 hover:to-emerald-400 shadow-xl shadow-blue-600/30 transition-all duration-300 cursor-pointer">
                        Start Free Trial ✨
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </motion.div>
            </div>

            {/* Ambient Background Graphic, similar to Home */}
            <div className="absolute inset-0 pointer-events-none z-0 opacity-30 dark:opacity-20 overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/20 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/20 rounded-full blur-[120px]"></div>
            </div>
        </div>
    );
}

function FeatureCard({ feature, index }) {
    const bgColors = {
        blue: "bg-blue-500/[0.02] dark:bg-white/[0.01] hover:bg-blue-500/[0.06]",
        emerald: "bg-emerald-500/[0.02] dark:bg-white/[0.01] hover:bg-emerald-500/[0.06]",
        purple: "bg-purple-500/[0.02] dark:bg-white/[0.01] hover:bg-purple-500/[0.06]",
        pink: "bg-pink-500/[0.02] dark:bg-white/[0.01] hover:bg-pink-500/[0.06]",
        amber: "bg-amber-500/[0.02] dark:bg-white/[0.01] hover:bg-amber-500/[0.06]",
        indigo: "bg-indigo-500/[0.02] dark:bg-white/[0.01] hover:bg-indigo-500/[0.06]",
    };

    const textColors = {
        blue: "text-blue-600 dark:text-blue-400",
        emerald: "text-emerald-600 dark:text-emerald-400",
        purple: "text-purple-600 dark:text-purple-400",
        pink: "text-pink-600 dark:text-pink-400",
        amber: "text-amber-600 dark:text-amber-400",
        indigo: "text-indigo-600 dark:text-indigo-400",
    };

    const iconBg = {
        blue: "bg-blue-500/10 dark:bg-blue-500/20",
        emerald: "bg-emerald-500/10 dark:bg-emerald-500/20",
        purple: "bg-purple-500/10 dark:bg-purple-500/20",
        pink: "bg-pink-500/10 dark:bg-pink-500/20",
        amber: "bg-amber-500/10 dark:bg-amber-500/20",
        indigo: "bg-indigo-500/10 dark:bg-indigo-500/20",
    }

    const isWide = feature.isWide;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className={`${feature.colSpan}`}
        >
            <Link
                to={feature.path}
                className={`relative group h-full rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 ${bgColors[feature.color]} overflow-hidden block cursor-pointer`}
            >
                <div className={`flex flex-col h-full rounded-xl relative z-10 ${isWide ? 'md:flex-row md:items-center md:gap-8' : ''}`}>

                    {/* Content Section */}
                    <div className={`flex flex-col ${isWide ? 'md:w-1/2' : 'h-full'}`}>
                        {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                        <div className={`p-3 rounded-xl ${iconBg[feature.color]} ${textColors[feature.color]}`}>
                            {feature.icon}
                        </div>
                        <div className="px-3 py-1 rounded-full bg-slate-500/10 dark:bg-white/5 text-xs text-secondary font-medium">
                            {feature.metric}
                        </div>
                    </div>

                    <div className="mb-8">
                        <h3 className="text-xl font-bold text-primary mb-2">{feature.title}</h3>
                        <p className="text-secondary leading-relaxed text-sm mb-6">
                            {feature.desc}
                        </p>

                        <ul className="space-y-3">
                            {feature.items.map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-sm text-secondary">
                                    <CheckCircle2 className={`w-4 h-4 ${textColors[feature.color]}`} />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* For non-wide cards, visual is at bottom */}
                    {!isWide && (
                        <div className="mt-auto pt-6">
                            <CardVisual id={feature.id} color={feature.color} />
                        </div>
                    )}
                </div>

                {/* For wide cards, visual is on the right */}
                {isWide && (
                    <div className="mt-8 md:mt-0 md:w-1/2 h-full min-h-[200px] rounded-2xl bg-slate-500/[0.04] dark:bg-white/[0.02] p-6 flex items-center justify-center">
                        <CardVisual id={feature.id} color={feature.color} />
                    </div>
                )}

            </div>

            {/* Ambient Background Glow */}
            <div className={`absolute -right-20 -bottom-20 w-64 h-64 rounded-full blur-3xl opacity-10 bg-${feature.color}-500 pointer-events-none`}></div>
            </Link>
        </motion.div>
    );
}

function CardVisual({ id, color }) {
    // Dynamic color maps because Tailwind can't interpolate strings like "bg-${color}-500"
    const colorMap = {
        blue: { bg: "bg-blue-500", bgLight: "bg-blue-500/20", bgMid: "bg-blue-500/50" },
        emerald: { bg: "bg-emerald-500", bgLight: "bg-emerald-500/20", bgMid: "bg-emerald-500/50" },
        purple: { bg: "bg-purple-500", bgLight: "bg-purple-500/20", bgMid: "bg-purple-500/50" },
        pink: { bg: "bg-pink-500", bgLight: "bg-pink-500/20", bgMid: "bg-pink-500/50" },
        amber: { bg: "bg-amber-500", bgLight: "bg-amber-500/20", bgMid: "bg-amber-500/50" },
        indigo: { bg: "bg-indigo-500", bgLight: "bg-indigo-500/20", bgMid: "bg-indigo-500/50" },
    };
    const c = colorMap[color] || colorMap.blue;

    if (id === 'accounting') {
        return (
            <div className="flex items-end gap-2 h-[72px] w-full opacity-80 pt-4">
                {[40, 70, 45, 90, 65, 85, 55, 95].map((h, i) => (
                    <div key={i} className={`flex-1 rounded-t-sm ${c.bg}`} style={{ height: `${h}%`, opacity: 0.3 + (i * 0.1) }}></div>
                ))}
            </div>
        )
    }
    if (id === 'expenses') {
        return (
            <div className="flex items-center gap-4 group-hover:translate-x-2 transition-transform h-[72px]">
                <div className="w-12 h-12 rounded-full border-4 border-emerald-500/30 border-t-emerald-500 animate-spin flex-shrink-0"></div>
                <div className="space-y-2 w-full">
                    <div className="h-2 w-2/3 bg-emerald-500/20 rounded"></div>
                    <div className="h-2 w-1/2 bg-emerald-500/20 rounded"></div>
                </div>
            </div>
        )
    }
    // Generic filler
    return (
        <div className="flex gap-4 h-[72px] items-center opacity-70 group-hover:opacity-100 transition-opacity w-full group-hover:translate-x-2 duration-300">
            <div className={`w-8 h-8 rounded-full ${c.bgLight} flex-shrink-0`}></div>
            <div className={`w-full h-2 rounded-full ${c.bgLight} overflow-hidden`}>
                <div className={`h-full w-2/3 ${c.bgMid} rounded-full`}></div>
            </div>
        </div>
    )
}
